import React, { useState, useEffect } from 'react';
import KamokuSelectWithHojo from './KamokuSelectWithHojo';

interface SubLedgerEntry {
    date: string;
    denpyou_bangou: string;
    description: string;
    debit_amount: number;
    credit_amount: number;
    balance: number;
}

interface SubLedgerResponse {
    kamoku_name: string;
    entries: SubLedgerEntry[];
    total_debit: number;
    total_credit: number;
    start_balance: number;
    end_balance: number;
}

interface Kamoku {
    id: number;
    kamoku_code: string;
    kamoku: string;
    kamoku_yomi?: string;
    hojo_kamoku_ari?: boolean;
}

interface SubLedgerProps {
    initialKamokuId?: number;
    initialDate?: string;
    apiFetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId?: number | null;
}

const SubLedger: React.FC<SubLedgerProps> = ({ initialKamokuId, initialDate, apiFetch, currentTenantId }) => {
    const [kamokuList, setKamokuList] = useState<Kamoku[]>([]);
    const [selectedKamokuId, setSelectedKamokuId] = useState<string>(initialKamokuId ? String(initialKamokuId) : '');
    const [selectedHojoId, setSelectedHojoId] = useState<string | undefined>(undefined);
    const [startDate, setStartDate] = useState<string>(initialDate || '2025-04-01');
    const [endDate, setEndDate] = useState<string>('2026-03-31');
    const [data, setData] = useState<SubLedgerResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchKamokuList();
    }, [currentTenantId]);

    const fetchKamokuList = async () => {
        if (!apiFetch) {
            setError('データ取得関数が設定されていません。');
            return;
        }
        try {
            const response = await apiFetch('/api/kamoku');
            const data = await response.json();
            setKamokuList(data || []);
        } catch (err) {
            console.error('Failed to fetch kamoku list', err);
            setError('勘定科目の取得に失敗しました。');
        }
    };

    const handleSearch = async () => {
        if (!selectedKamokuId) {
            setError('勘定科目を選択してください。');
            return;
        }

        if (!apiFetch) {
            setError('データ取得関数が設定されていません。');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                kamoku_id: selectedKamokuId,
                start_date: startDate,
                end_date: endDate,
            });
            if (selectedHojoId) {
                params.append('hojo_id', selectedHojoId);
            }
            
            const response = await apiFetch(`/api/sub-ledger/entries?${params}`);
            if (!response.ok) {
                throw new Error('データの取得に失敗しました。');
            }
            const data = await response.json();
            setData(data);
        } catch (err: any) {
            console.error('Failed to fetch sub ledger data', err);
            setError(err.message || '補助元帳データの取得に失敗しました。');
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const handlePdfOutput = async () => {
        if (!selectedKamokuId) {
            setError('勘定科目を選択してください。');
            return;
        }

        if (!apiFetch) {
            setError('データ取得関数が設定されていません。');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                kamoku_id: selectedKamokuId,
                start_date: startDate,
                end_date: endDate,
            });
            if (selectedHojoId) {
                params.append('hojo_id', selectedHojoId);
            }

            const response = await apiFetch(`/api/sub-ledger/pdf?${params}`);
            if (!response.ok) {
                throw new Error('PDFの生成に失敗しました。');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `sub_ledger_${startDate}_${selectedKamokuId}${selectedHojoId ? '_' + selectedHojoId : ''}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error('Failed to generate PDF', err);
            setError(err.message || 'PDFの出力に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    const handleKamokuChange = (kamokuId: string, hojoId?: string) => {
        setSelectedKamokuId(kamokuId);
        setSelectedHojoId(hojoId);
    };

    return (
        <div style={{ padding: '20px', fontFamily: "'MS UI Gothic', sans-serif" }}>
            <h2 style={{ fontSize: '18px', marginBottom: '20px', borderBottom: '1px solid #ccc', paddingBottom: '5px' }}>
                補助元帳
            </h2>

            <div style={{
                backgroundColor: '#f5f5f5',
                padding: '15px',
                border: '1px solid #ddd',
                marginBottom: '20px',
                display: 'flex',
                gap: '15px',
                alignItems: 'center',
                flexWrap: 'wrap'
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '300px' }}>
                    <label style={{ fontSize: '12px', marginBottom: '5px' }}>勘定科目・補助科目</label>
                    <KamokuSelectWithHojo
                        kamokuList={kamokuList}
                        value={selectedKamokuId ? Number(selectedKamokuId) : null}
                        hojoValue={selectedHojoId ? Number(selectedHojoId) : null}
                        onChange={handleKamokuChange}
                        apiFetch={apiFetch}
                        currentTenantId={currentTenantId ?? undefined}
                        placeholder="科目を選択（コードまたは名称で検索）"
                    />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={{ fontSize: '12px', marginBottom: '5px' }}>開始日</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        style={{ padding: '4px' }}
                    />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={{ fontSize: '12px', marginBottom: '5px' }}>終了日</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        style={{ padding: '4px' }}
                    />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={handleSearch}
                        disabled={loading}
                        style={{
                            padding: '6px 15px',
                            marginTop: '18px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1
                        }}
                    >
                        {loading ? '検索中...' : '検索'}
                    </button>

                    <button
                        onClick={handlePdfOutput}
                        disabled={loading}
                        style={{
                            padding: '6px 15px',
                            marginTop: '18px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1
                        }}
                    >
                        {loading ? '処理中...' : 'PDF出力'}
                    </button>
                </div>
            </div>

            {error && (
                <div style={{
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    padding: '10px',
                    marginBottom: '20px',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px'
                }}>
                    {error}
                </div>
            )}

            {data && (
                <div style={{ border: '1px solid #ddd', padding: '15px', backgroundColor: 'white' }}>
                    <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>
                        {data.kamoku_name} ({startDate} 〜 {endDate})
                    </h3>

                    <div style={{ display: 'flex', gap: '20px', marginBottom: '15px', fontSize: '14px', fontWeight: 'bold' }}>
                        <div>開始残高: {data.start_balance.toLocaleString()}</div>
                        <div>借方合計: {data.total_debit.toLocaleString()}</div>
                        <div>貸方合計: {data.total_credit.toLocaleString()}</div>
                        <div>終了残高: {data.end_balance.toLocaleString()}</div>
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#e9ecef' }}>
                                <th style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'left' }}>日付</th>
                                <th style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'left' }}>伝票番号</th>
                                <th style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'left' }}>摘要</th>
                                <th style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>借方金額</th>
                                <th style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>貸方金額</th>
                                <th style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>残高</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.entries.length === 0 ? (
                                <tr>
                                    <td colSpan={6} style={{ border: '1px solid #dee2e6', padding: '15px', textAlign: 'center' }}>
                                        データがありません
                                    </td>
                                </tr>
                            ) : (
                                data.entries.map((entry, index) => (
                                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px' }}>{entry.date}</td>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px' }}>{entry.denpyou_bangou}</td>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px' }}>{entry.description}</td>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>
                                            {entry.debit_amount > 0 ? entry.debit_amount.toLocaleString() : ''}
                                        </td>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>
                                            {entry.credit_amount > 0 ? entry.credit_amount.toLocaleString() : ''}
                                        </td>
                                        <td style={{ border: '1px solid #dee2e6', padding: '8px', textAlign: 'right' }}>
                                            {entry.balance.toLocaleString()}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SubLedger;
